import { ComponentType, useState } from "react";
import MuiDrawer from "@mui/material/Drawer";
import GB from "country-flag-icons/react/3x2/GB";
import PL from "country-flag-icons/react/3x2/PL";
import { useTranslation } from "react-i18next";
import IconButton from "../IconButton/IconButton";
import PaymentMethodTile from "../PaymentMethodTile/PaymentMethodTile";
import Grid from "../../config/Grid/Grid";
import { drawerStyles } from "../Drawer/Drawer.styles";
import drawerCss from "../Drawer/Drawer.module.scss";
import { supportedLanguages } from "../../lib/localisation/config";
import styles from "./LanguageSwitch.module.scss";

interface LanguageSwitchProps {
  dark?: boolean;
}

/**
 * Each language named in itself, so a guest who reads none of the others can
 * still find theirs.
 *
 * Flags come from country-flag-icons, imported one at a time: its CSS-based
 * alternatives pull every country in the world into the bundle, which is a
 * poor trade for a list this short. The flag is of a country, the language is
 * not, hence English carrying GB.
 */
const LANGUAGES: Record<
  string,
  { name: string; Flag: ComponentType<{ className?: string }> }
> = {
  en: { name: "English", Flag: GB },
  pl: { name: "Polski", Flag: PL },
};

/**
 * Opens a sheet listing every language the app carries.
 *
 * The two-letter toggle it replaces only worked while there were two
 * languages, and said nothing about what they were.
 */
function LanguageSwitch(props: LanguageSwitchProps) {
  const { dark } = props;
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleLanguageChange = (language: string) => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    setOpen(false);
  };

  return (
    <>
      <IconButton
        iconName="las la-globe icon-big"
        onClick={() => setOpen(true)}
        color={dark ? "secondary" : "primary"}
      />

      <MuiDrawer
        sx={drawerStyles}
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Grid>
          <div
            className={`start-1 col-4 align-start ${drawerCss.drawerLayoutWrapper}`}
          >
            <div className={drawerCss.drawerLayoutHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={() => setOpen(false)}
                color="primary"
              />
              <p className={`body-bold-large ${drawerCss.drawerLayoutTitle}`}>
                {t("languages.title")}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>

            <div className={styles.languageList}>
              {supportedLanguages.map((language) => {
                const Flag = LANGUAGES[language]?.Flag;
                return (
                  <PaymentMethodTile
                    key={language}
                    paymentMethodName={LANGUAGES[language]?.name ?? language}
                    paymentMethodIcon={
                      Flag ? <Flag className={styles.languageFlag} /> : null
                    }
                    selected={i18n.language === language}
                    onSelect={() => handleLanguageChange(language)}
                  />
                );
              })}
            </div>
          </div>
        </Grid>
      </MuiDrawer>
    </>
  );
}

export default LanguageSwitch;
